/**
 * The quick-filter values that are OBJECTS rather than id arrays, and the
 * readers that parse them.
 *
 * A shape is read by the control that edits it, by the panel's value index
 * (search + applied chips) and by `build-where`, so it lives here instead of
 * with any one of those. Every reader is defensive: a value can arrive from a
 * saved view written against a shape the panel no longer writes, and a bare id
 * array must come back empty rather than throw.
 */

/**
 * `{ userIds, teamIds }` under the `assignee` field: what a saved view holds
 * from when ONE Assignee filter took people or anyone-on-a-team. Nothing writes
 * it any more — Assignee is the people, and a team's work is the Teams
 * property — but stored states still carry it, so `build-where` and
 * `migrateStoredFilterState` both read it through here.
 */
type AssigneePrincipalValue = {
  userIds: string[];
  teamIds: string[];
};

const EMPTY_ASSIGNEE: AssigneePrincipalValue = { userIds: [], teamIds: [] };

export function parseAssigneePrincipalValue(
  value: unknown,
): AssigneePrincipalValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return EMPTY_ASSIGNEE;
  }
  const { userIds, teamIds } = value as Partial<AssigneePrincipalValue>;
  return {
    userIds: Array.isArray(userIds) ? userIds : [],
    teamIds: Array.isArray(teamIds) ? teamIds : [],
  };
}

/**
 * Workflow/stage: also one value, but its halves nest rather than sit
 * parallel — they decompose into ANDed conditions, so a stage is only ever
 * asked about within its own workflow.
 */
type WorkflowStageValue = {
  workflowIds: string[];
  stageIds: string[];
};

const EMPTY_WORKFLOW_STAGE: WorkflowStageValue = {
  workflowIds: [],
  stageIds: [],
};

export function parseWorkflowStageValue(value: unknown): WorkflowStageValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return EMPTY_WORKFLOW_STAGE;
  }
  const { workflowIds, stageIds } = value as Partial<WorkflowStageValue>;
  return {
    workflowIds: Array.isArray(workflowIds) ? workflowIds : [],
    stageIds: Array.isArray(stageIds) ? stageIds : [],
  };
}
